import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@heroicons/react/24/outline";
import BucketAction from "../../actions/BucketAction";
import DataBucketRow, { ColorDot, getNextAvailableColor } from "./DataBucketRow";
import DataBucketRowSkeleton from "./DataBucketRowSkeleton";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";

type Props = {
  title: string;
  projectId: string;
  type: string;
};

const DataBucketList = ({ title, projectId, type }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const buckets = useSelector((state: any) => state.bucketReducer.get(type)) || fromJS([]);

  const [activeItemId, setActiveItemId] = useState<string>("");
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [editingColors, setEditingColors] = useState<Record<string, string>>({});
  const [newValue, setNewValue] = useState("");
  const [newColor, setNewColor] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reorderingBucketId, setReorderingBucketId] = useState<string | null>(null);
  const [reorderingDirection, setReorderingDirection] = useState<"up" | "down" | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [bucketIdToDelete, setBucketIdToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      setIsLoading(true);
      const dispatchPromise = dispatch(BucketAction.getBuckets(projectId, type) as any);

      if (dispatchPromise && typeof dispatchPromise.then === "function") {
        dispatchPromise.finally(() => {
          setIsLoading(false);
        });
      } else {
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      }
    }
  }, [dispatch, projectId, type]);

  const bucketItems = useMemo(
    () => (typeof buckets.toJS === "function" ? buckets.toJS() : buckets),
    [buckets]
  );

  // Sync editing values and colors from store
  useEffect(() => {
    const nextValues: Record<string, string> = {};
    const nextColors: Record<string, string> = {};
    bucketItems.forEach((item: any) => {
      const bucketId = item.id || item.get?.("id");
      const bucketName = item.name || item.get?.("name") || "";
      const bucketColor = item.color || item.get?.("color") || "";
      if (bucketId) {
        nextValues[bucketId] = bucketName;
        nextColors[bucketId] = bucketColor;
      }
    });
    setEditingValues(nextValues);
    setEditingColors(nextColors);
  }, [bucketItems]);

  const handleSaveBucket = useCallback(
    ({ value, id, color }: { value: string; id?: string; color?: string }) => {
      if (!projectId || !value) {
        return;
      }

      const bucket =
        buckets.filter((item: any) => item.get("id") === id).first() || fromJS({ id: "-1" });

      let payload = {
        ...bucket.toJS(),
        name: value,
        color: color ?? bucket.get("color") ?? "",
        projectId,
        type,
        setUp: false,
      };

      if (bucket.get("id") === "-1" || !id) {
        payload = {
          id: undefined,
          projectId,
          tenantId: "",
          name: value,
          color: color || "",
          order: buckets.size,
          type,
          setUp: false,
        };
      }

      dispatch(BucketAction.saveBucket(payload));
    },
    [buckets, dispatch, projectId, type]
  );

  const handleFinish = useCallback(
    (value: string, id?: string, color?: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        if (!id) {
          setNewValue("");
          setNewColor("");
        }
        setActiveItemId("");
        return;
      }
      handleSaveBucket({ value: trimmed, id, color });
      if (!id) {
        setNewValue("");
        setNewColor("");
      }
      setActiveItemId("");
    },
    [handleSaveBucket]
  );

  const handleColorChange = useCallback(
    (bucketId: string, color: string) => {
      setEditingColors((prev) => ({ ...prev, [bucketId]: color }));
      // Auto-save color change immediately
      const name = editingValues[bucketId];
      if (name && bucketId) {
        handleSaveBucket({ value: name, id: bucketId, color });
      }
    },
    [editingValues, handleSaveBucket]
  );

  const handleAdd = useCallback(() => {
    // Auto-assign next available color that doesn't duplicate existing buckets
    const usedColors = bucketItems.map((item: any) => item.color || item.get?.("color") || "");
    const nextColor = getNextAvailableColor(usedColors);
    setActiveItemId("new");
    setNewValue("");
    setNewColor(nextColor);
  }, [bucketItems]);

  const handleDelete = useCallback((bucketId: string) => {
    setBucketIdToDelete(bucketId);
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (bucketIdToDelete) {
      dispatch(BucketAction.deleteBucket(bucketIdToDelete, type, false));
      setShowDeleteConfirm(false);
      setBucketIdToDelete(null);
    }
  }, [bucketIdToDelete, dispatch, type]);

  const handleReorder = useCallback(
    (bucketId: string, direction: "up" | "down") => {
      if (!projectId || reorderingBucketId) {
        return;
      }
      setReorderingBucketId(bucketId);
      setReorderingDirection(direction);
      const dispatchPromise = dispatch(
        BucketAction.changeBucketOrder({
          projectId,
          bucketId,
          direction,
          type,
          setUp: false,
        }) as any
      );

      if (dispatchPromise && typeof dispatchPromise.then === "function") {
        dispatchPromise
          .then(() => {
            dispatch(BucketAction.getBuckets(projectId, type));
          })
          .finally(() => {
            setReorderingBucketId(null);
            setReorderingDirection(null);
          });
      } else {
        setTimeout(() => {
          setReorderingBucketId(null);
          setReorderingDirection(null);
        }, 100);
      }
    },
    [dispatch, projectId, type, reorderingBucketId]
  );

  const isEmpty = bucketItems.length === 0;

  return (
    <section className="rounded-2xl">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <button
          type="button"
          onClick={handleAdd}
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>

      {isLoading ? (
        <DataBucketRowSkeleton count={3} rowClassName="px-4 py-2" />
      ) : isEmpty && activeItemId !== "new" ? (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
          {t("No records")}
        </div>
      ) : (
        <div className="space-y-3">
          {bucketItems.map((item: any, index: number) => {
            const bucketId = item.id || item.get?.("id");
            const rowKey = bucketId || item.temporaryId || `${item?.name || "bucket"}-${index}`;

            const bucketValue = editingValues[bucketId] ?? item.name ?? item.get?.("name") ?? "";
            const bucketColor = editingColors[bucketId] ?? item.color ?? item.get?.("color") ?? "";

            const isReordering = reorderingBucketId === bucketId;
            const isReorderingUp = isReordering && reorderingDirection === "up";
            const isReorderingDown = isReordering && reorderingDirection === "down";

            return (
              <DataBucketRow
                key={`${rowKey}-${index}`}
                index={index}
                bucketId={bucketId}
                bucketValue={bucketValue}
                bucketColor={bucketColor}
                placeholder="Name"
                isActive={activeItemId === bucketId}
                className="px-4 py-2"
                bodyClassName="items-center"
                isLoading={isLoading}
                isReordering={isReordering}
                isReorderingUp={isReorderingUp}
                isReorderingDown={isReorderingDown}
                canMoveUp={index > 0}
                canMoveDown={index < bucketItems.length - 1}
                onInputChange={(value) =>
                  setEditingValues((prev) => ({
                    ...prev,
                    [bucketId]: value,
                  }))
                }
                onColorChange={(color) => handleColorChange(bucketId, color)}
                onInputFocus={() => setActiveItemId(bucketId)}
                onInputBlur={() => handleFinish(bucketValue, bucketId, bucketColor)}
                onInputKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleFinish(bucketValue, bucketId, bucketColor);
                  }
                }}
                onReorderUp={() => handleReorder(bucketId, "up")}
                onReorderDown={() => handleReorder(bucketId, "down")}
                onDelete={() => handleDelete(bucketId)}
                t={t}
              />
            );
          })}

          {activeItemId === "new" && (
            <div className="flex items-center gap-1 rounded-2xl border border-dashed border-indigo-200 px-4 py-2">
              <ColorDot color={newColor} onChange={(c) => setNewColor(c)} disabled={isLoading} />
              <input
                value={newValue}
                autoFocus
                placeholder={t("Name")}
                disabled={isLoading}
                onChange={(event) => setNewValue(event.target.value)}
                onBlur={() => handleFinish(newValue, undefined, newColor)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleFinish(newValue, undefined, newColor);
                  }
                }}
                className="flex-1 rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        visible={showDeleteConfirm}
        title={t("Delete")}
        description={t("Are you sure you want to delete this item? This action cannot be undone.")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleConfirmDelete}
        onClose={() => {
          setShowDeleteConfirm(false);
          setBucketIdToDelete(null);
        }}
        dangerBtn={true}
      />
    </section>
  );
};

export default DataBucketList;
